import { AnimateModelIndex } from '../../../mol-plugin-state/animation/built-in/model-index';
import { PluginStateObject } from '../../../mol-plugin-state/objects';
import { StateTransforms } from '../../../mol-plugin-state/transforms';
import { PluginContext } from '../../../mol-plugin/context';
import { StateSelection } from '../../../mol-state';
import { failure, success, toMcpResult } from '../result';
import { ToolDefinition, ToolResponse, WebMcpValue } from '../registration';
import { runWebMcpMutation, WebMcpMutationServices } from './safety';

interface TrajectoryFrame {
    readonly ref: string
    readonly frame: number
    readonly frameCount: number
}

interface CoordinatesMetadata {
    readonly ref: string
    readonly label: string
    readonly frameCount: number
    readonly atomCount: number
    readonly time: {
        readonly available: boolean
        readonly unit: 'ps' | 'step'
        readonly delta: number
        readonly offset: number
        readonly first: number
        readonly last: number
    }
    readonly cell: { readonly available: boolean }
    readonly velocities: { readonly available: boolean }
    readonly forces: { readonly available: boolean }
}

function failed(error: string, hint: string): ToolResponse { return toMcpResult(failure(error, hint)); }
function isNumber(value: WebMcpValue | undefined): value is number { return typeof value === 'number'; }
function isString(value: WebMcpValue | undefined): value is string { return typeof value === 'string'; }
function models(plugin: PluginContext) { return plugin.state.data.select(StateSelection.Generators.ofTransformer(StateTransforms.Model.ModelFromTrajectory)); }

function coordinatesMetadata(plugin: PluginContext): readonly CoordinatesMetadata[] {
    return plugin.state.data.select(StateSelection.Generators.ofType(PluginStateObject.Molecule.Coordinates)).flatMap(cell => {
        const object = cell.obj;
        if (!object || object.data.frames.length === 0) return [];
        const coordinates = object.data;
        const first = coordinates.frames[0].time.value;
        const last = coordinates.frames[coordinates.frames.length - 1].time.value;
        return [{
            ref: cell.transform.ref,
            label: object.label,
            frameCount: coordinates.frames.length,
            atomCount: coordinates.frames[0].elementCount,
            time: { available: coordinates.deltaTime.value !== 0 || coordinates.timeOffset.value !== 0 || first !== 0 || last !== 0, unit: coordinates.deltaTime.unit, delta: coordinates.deltaTime.value, offset: coordinates.timeOffset.value, first, last },
            cell: { available: coordinates.hasCell },
            velocities: { available: coordinates.hasVelocities },
            forces: { available: coordinates.hasForces },
        }];
    });
}

function frames(plugin: PluginContext): readonly TrajectoryFrame[] {
    return models(plugin).flatMap(model => {
        const trajectory = StateSelection.findAncestorOfType(plugin.state.data.tree, plugin.state.data.cells, model.transform.ref, PluginStateObject.Molecule.Trajectory);
        return trajectory?.obj ? [{ ref: model.transform.ref, frame: (model.transform.params?.modelIndex ?? 0) + 1, frameCount: trajectory.obj.data.frameCount }] : [];
    });
}

async function setFrame(plugin: PluginContext, services: WebMcpMutationServices | undefined, frame: number): Promise<ToolResponse> {
    if (!Number.isInteger(frame) || frame < 1) return failed('Invalid trajectory frame.', 'Provide a positive integer frame number.');
    const current = frames(plugin);
    if (current.length === 0) return failed('No trajectory is loaded.', 'Load a multi-frame structure or trajectory first.');
    const outOfRange = current.find(entry => frame > entry.frameCount);
    if (outOfRange) return failed(`Frame ${frame} is out of range.`, `Choose a frame from 1 to ${outOfRange.frameCount}.`);
    if (!services) return failed('Trajectory updates require WebMCP safety services.', 'Enable the WebMCP safety integration, then retry.');
    const outcome = await runWebMcpMutation({ actor: 'agent', tool: 'set-trajectory-frame', args: { action: 'set-trajectory-frame', target: String(frame) }, summary: `Trajectory frame set to ${frame}.` }, services, async () => {
        const update = plugin.state.data.build();
        for (const model of models(plugin)) update.to(model).update({ modelIndex: frame - 1 });
        await update.commit({ canUndo: 'Set trajectory frame' });
    });
    return outcome.ok ? toMcpResult(success(outcome.summary, { type: 'trajectory', frames: frames(plugin), isPlaying: plugin.managers.animation.isAnimating })) : failed(outcome.error, outcome.hint);
}

/** Trajectory controls operate only on trajectory-backed models and preserve full undo snapshots. */
export function createTrajectoryTools(plugin: PluginContext, services: WebMcpMutationServices): readonly ToolDefinition[] {
    const available = (capabilities: Parameters<NonNullable<ToolDefinition['isAvailable']>>[0]) => capabilities.hasTrajectory;
    return [
        {
            name: 'get-trajectory-state', description: 'Return loaded trajectory frames and playback state.', inputSchema: { type: 'object' }, annotations: { readOnlyHint: true }, isAvailable: available,
            execute: () => {
                const current = frames(plugin);
                return current.length === 0 ? failed('No trajectory is loaded.', 'Load a multi-frame structure or trajectory first.') : toMcpResult(success('Trajectory state returned.', { type: 'trajectory', frames: current, isPlaying: plugin.managers.animation.isAnimating }));
            }
        },
        {
            name: 'get-trajectory-metadata', description: 'Return time, cell, velocity, and force availability from loaded Coordinates state cells.', inputSchema: { type: 'object', additionalProperties: false }, annotations: { readOnlyHint: true }, isAvailable: capabilities => capabilities.hasTrajectory || capabilities.hasCoordinates,
            execute: () => {
                const metadata = coordinatesMetadata(plugin);
                return metadata.length === 0
                    ? failed('Trajectory coordinate metadata is unavailable.', 'Load coordinate data such as DCD, XTC, TRR, or NCTRAJ, then retry.')
                    : toMcpResult(success('Trajectory coordinate metadata returned.', { type: 'trajectory-metadata', coordinates: metadata }));
            }
        },
        {
            name: 'set-trajectory-frame', description: 'Set the one-based frame on all loaded trajectory models.', inputSchema: { type: 'object', properties: { frame: { type: 'integer', minimum: 1 } }, required: ['frame'], additionalProperties: false }, isAvailable: available,
            execute: input => isNumber(input.frame) ? setFrame(plugin, services, input.frame) : failed('Invalid trajectory frame.', 'Provide a positive integer frame number.')
        },
        {
            name: 'play-trajectory', description: 'Play the loaded trajectory using Mol* animation.', inputSchema: { type: 'object', properties: { fps: { type: 'number', minimum: 5, maximum: 250 }, direction: { type: 'string', enum: ['forward', 'backward'] } }, additionalProperties: false }, isAvailable: available,
            execute: async input => {
                if (frames(plugin).length === 0) return failed('No trajectory is loaded.', 'Load a multi-frame structure or trajectory first.');
                if (!services) return failed('Trajectory playback requires WebMCP safety services.', 'Enable the WebMCP safety integration, then retry.');
                const fps = isNumber(input.fps) ? input.fps : 30;
                if (fps < 5 || fps > 250) return failed('Invalid playback rate.', 'Use an FPS value from 5 to 250.');
                const direction = isString(input.direction) && input.direction === 'backward' ? 'backward' : 'forward';
                const outcome = await runWebMcpMutation({ actor: 'agent', tool: 'play-trajectory', args: { action: 'play-trajectory', target: `${fps}:${direction}` }, summary: `Trajectory animation started at ${fps} FPS.` }, services, () => plugin.managers.animation.play(AnimateModelIndex, { mode: { name: 'loop', params: { direction } }, duration: { name: 'computed', params: { targetFps: fps } } }));
                return outcome.ok ? toMcpResult(success(outcome.summary, { type: 'trajectory', frames: frames(plugin), isPlaying: plugin.managers.animation.isAnimating })) : failed(outcome.error, outcome.hint);
            }
        },
        {
            name: 'stop-animation', description: 'Stop the current Mol* animation.', inputSchema: { type: 'object' }, isAvailable: available,
            execute: async () => {
                if (!services) return failed('Stopping animation requires WebMCP safety services.', 'Enable the WebMCP safety integration, then retry.');
                const outcome = await runWebMcpMutation({ actor: 'agent', tool: 'stop-animation', args: { action: 'stop-animation' }, summary: 'Animation stopped.' }, services, () => plugin.managers.animation.stop());
                return outcome.ok ? toMcpResult(success(outcome.summary, { type: 'trajectory', frames: frames(plugin), isPlaying: plugin.managers.animation.isAnimating })) : failed(outcome.error, outcome.hint);
            }
        },
        {
            name: 'set-model', description: 'Set the one-based model index on all trajectory-backed models.', inputSchema: { type: 'object', properties: { model: { type: 'integer', minimum: 1 } }, required: ['model'], additionalProperties: false }, isAvailable: available,
            execute: input => isNumber(input.model) ? setFrame(plugin, services, input.model) : failed('Invalid model index.', 'Provide a positive integer model index.')
        },
    ];
}
